import { useRef } from "react";

import { UseDoubleTapOptions, UseDoubleTapReturn } from "./types";

const DEFAULT_DELAY = 200; // ms

/**
 * Custom hook for handling double-tap interactions
 *
 * This hook provides double-tap detection that allows single taps to propagate
 * through to parent handlers, unlike library solutions that capture all clicks.
 * This is useful for scenarios where you want single clicks to trigger one action
 * (like dismissing controls) while double clicks trigger another (like skipping).
 *
 * @param options - Configuration options for the double-tap behavior
 * @returns Object containing the onClick handler to attach to your element
 *
 * @example
 * ```tsx
 * const { onClick } = useDoubleTap({
 *   delay: 300,
 *   onDoubleTap: (e) => console.log('Double tapped!'),
 * });
 *
 * return <div onClick={onClick}>Double tap me</div>;
 * ```
 */
const useDoubleTap = ({
  delay = DEFAULT_DELAY,
  onDoubleTap,
}: UseDoubleTapOptions): UseDoubleTapReturn => {
  const lastTapRef = useRef<number>(0);

  const onClick = (event: React.MouseEvent) => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;

    if (timeSinceLastTap < delay && timeSinceLastTap > 0) {
      // Double tap detected - stop propagation and execute callback
      event.stopPropagation();
      onDoubleTap(event);
      lastTapRef.current = 0; // Reset to prevent triple-tap
    } else {
      // First tap - don't stop propagation, allow event to bubble
      lastTapRef.current = now;
    }
  };

  return { onClick };
};

export { useDoubleTap };
